// Phase 34 §1 — the rules behind the unified queue.
//
// The screen's whole argument is ONE queue ranked by consequence: a voicemail
// breaches the same way an email does, so it sorts the same way. That is only
// honest because every row here is a ticket — one table, one endpoint, already
// ordered `sortBy: 'sla_priority'` server-side. Nothing on this page merges
// channels in the browser.
//
// Kept out of the component so the two invariants the plan calls out — nulls
// sort last, and a missing policy never renders as '0m' — are testable.

import type { InboxItem } from '@services/inbox.service';
import { duration } from '@shared/format/duration';

/** The three channels the screen argues about, plus everything else. */
export type QueueChannel = 'email' | 'chat' | 'voice' | 'other';

// The tickets table stores `channel` as a free VarChar, so the aliases are
// real values in the data rather than a defensive flourish.
const VOICE_ALIASES = ['voice', 'phone', 'call', 'voicemail', 'telephony'];
const CHAT_ALIASES = ['chat', 'whatsapp', 'sms', 'messenger', 'telegram'];
const EMAIL_ALIASES = ['email', 'mail'];

/** Channel aliases, so a tab can filter the server by every spelling of itself. */
export const CHANNEL_FILTER: Record<Exclude<QueueChannel, 'other'>, string[]> = {
  email: EMAIL_ALIASES,
  chat: CHAT_ALIASES,
  voice: VOICE_ALIASES,
};

export function queueChannel(raw?: string | null): QueueChannel {
  const c = (raw || '').toLowerCase();
  if (EMAIL_ALIASES.includes(c)) return 'email';
  if (CHAT_ALIASES.includes(c)) return 'chat';
  if (VOICE_ALIASES.includes(c)) return 'voice';
  return 'other';
}

export const CHANNEL_GLYPH: Record<QueueChannel, string> = {
  email: '✉',
  chat: '◆',
  voice: '☎',
  other: '○',
};

/**
 * Folds the BFF's raw per-channel counts into the four buckets the tab strip
 * argues about. Every spelling of a channel lands in its own tab, and anything
 * the product does not name lands in `other` — so the four numbers always sum
 * to the queue total and a tab can never quietly drop a row it would show.
 *
 * Returns null when the server did not send the breakdown: the tabs then show
 * no count at all, which is the honest rendering of "not known".
 */
export function channelTally(
  byChannel?: Record<string, number>
): Record<QueueChannel, number> | null {
  if (!byChannel) return null;
  const tally: Record<QueueChannel, number> = { email: 0, chat: 0, voice: 0, other: 0 };
  for (const [raw, count] of Object.entries(byChannel)) {
    if (typeof count !== 'number' || Number.isNaN(count)) continue;
    tally[queueChannel(raw)] += count;
  }
  return tally;
}

export type SlaTone = 'danger' | 'warn' | 'normal' | 'none';

function dueTime(item: InboxItem): number | null {
  if (!item.slaDueAt) return null;
  const t = new Date(item.slaDueAt).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * A resolved or closed ticket has no live SLA clock — its targets were met (or
 * missed) at resolution and the countdown stopped there. The ticket list uses
 * the same gate (ticketsQueue.ts), so the Inbox must too: otherwise a resolved
 * item whose due time is simply in the past reads as "41m over" (#18942).
 */
function slaClockStopped(item: InboxItem): boolean {
  return item.status === 'resolved' || item.status === 'closed';
}

export function isBreached(item: InboxItem, now = Date.now()): boolean {
  if (slaClockStopped(item)) return false;
  if (item.slaBreached) return true;
  const due = dueTime(item);
  return due !== null && due < now;
}

/** 'none' is a real state — no policy matched — and must not read as 'due now'. */
export function slaTone(item: InboxItem, now = Date.now()): SlaTone {
  if (slaClockStopped(item)) return 'none';
  const due = dueTime(item);
  if (due === null) return 'none';
  if (isBreached(item, now)) return 'danger';
  return due - now <= 60 * 60 * 1000 ? 'warn' : 'normal';
}

/** '2h 40m over' · '18m left' · '—'. Never '0m over' for an item with no policy. */
export function slaLabel(item: InboxItem, now = Date.now()): string {
  if (slaClockStopped(item)) return '—';
  const due = dueTime(item);
  if (due === null) return '—';
  const seconds = Math.abs(due - now) / 1000;
  return isBreached(item, now)
    ? `${duration(seconds)} over`
    : `${duration(seconds)} left`;
}

/**
 * Oldest deadline first; no-policy items last, ordered among themselves by
 * arrival. The server already sorts the page this way — this guarantees the
 * nulls-last half of it, which `sla_priority` does not promise.
 */
/** Latest first (by when the ticket arrived); unparseable dates go last. */
export function byNewest(a: InboxItem, b: InboxItem): number {
  const ta = new Date(a.createdAt).getTime();
  const tb = new Date(b.createdAt).getTime();
  if (Number.isNaN(ta)) return Number.isNaN(tb) ? 0 : 1;
  if (Number.isNaN(tb)) return -1;
  return tb - ta;
}

export function bySlaThenReceived(a: InboxItem, b: InboxItem): number {
  const da = dueTime(a);
  const db = dueTime(b);
  if (da !== null && db !== null && da !== db) return da - db;
  if (da === null && db !== null) return 1;
  if (db === null && da !== null) return -1;
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

/** Same-day items show a clock; anything older shows the date it landed. */
export function receivedLabel(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const sameDay = new Date(now).toDateString() === d.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/** Requester line: the person, then the organisation if one is actually known. */
export function requesterLine(item: InboxItem): string {
  const name = item.customer?.name || item.customer?.email || 'Unknown sender';
  // There is no organisation on an inbox item — the BFF returns id/name/email
  // only, so the design's "ROHAN DESAI · MERIDIAN BANK" has no second half to
  // print. The ticket number is the fact that is actually there, and it is the
  // one an agent reads out loud.
  return item.ticketNumber ? `${name} · ${item.ticketNumber}` : name;
}
